import React, { useState, useEffect } from 'react';
import { FileText, Download, X, Loader2, BarChart3, Users, Tag, DollarSign, TrendingUp, Calendar, FileDown } from 'lucide-react';
import { projectsAPI, beneficiariesAPI, usersAPI } from '../services/api';
import api from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ReportsPage = ({ onClose }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchReportData(selectedProject.id);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('فشل تحميل المشاريع');
    }
  };

  const fetchReportData = async (projectId) => {
    setLoading(true);
    setError('');
    try {
      // Set project context for API calls
      const projectIdHeader = localStorage.getItem('currentProjectId');
      localStorage.setItem('currentProjectId', projectId.toString());

      try {
        // Fetch all data for the project
        const [beneficiariesRes, usersRes] = await Promise.all([
          api.get('/beneficiaries', { headers: { 'X-Project-ID': projectId.toString() } }),
          api.get('/users')
        ]);

        const beneficiaries = beneficiariesRes.data || [];
        const allUsers = usersRes.data || [];

        // Filter users by project
        const projectUsers = allUsers.filter(u => {
          // This is a simplified filter - in production, you'd want to check project_users table
          return true; // For now, show all users
        });

        // Fetch redemption data for all beneficiaries with redeemed coupons
        // Keep project context set during this operation
        const redemptionPromises = beneficiaries
          .filter(b => (b.redeemed_count || 0) > 0)
          .map(beneficiary => 
            api.get(`/redemptions/${beneficiary.id}`, { 
              headers: { 'X-Project-ID': projectId.toString() } 
            }).then(res => ({
              beneficiaryId: beneficiary.id,
              redemptions: res.data || []
            })).catch(err => {
              console.error(`Error fetching redemptions for beneficiary ${beneficiary.id}:`, err);
              return { beneficiaryId: beneficiary.id, redemptions: [] };
            })
          );

        const redemptionResults = await Promise.all(redemptionPromises);
        const redemptionsMap = {};
        redemptionResults.forEach(result => {
          redemptionsMap[result.beneficiaryId] = result.redemptions;
        });
        
        // Debug: Log redemption data
        console.log('Redemptions Map:', redemptionsMap);
        console.log('Total beneficiaries with redemptions:', Object.keys(redemptionsMap).length);
        console.log('Sample redemption:', redemptionResults[0]);

      // Calculate analytics
      const totalCoupons = beneficiaries.reduce((sum, b) => sum + (b.coupon_count || 0), 0);
      const redeemedCoupons = beneficiaries.reduce((sum, b) => sum + (b.redeemed_count || 0), 0);
      const pendingCoupons = totalCoupons - redeemedCoupons;
      const expiredCoupons = 0; // Calculate based on expiration date if available
      const totalCouponValue = beneficiaries.reduce((sum, b) => sum + ((b.coupon_count || 0) * (b.coupon_value || 0)), 0);
      const redeemedCouponValue = beneficiaries.reduce((sum, b) => sum + ((b.redeemed_count || 0) * (b.coupon_value || 0)), 0);
      const pendingCouponValue = totalCouponValue - redeemedCouponValue;

      // Vendor data
      const vendorList = projectUsers.filter(u => u.role === 'vendor');
      const adminList = projectUsers.filter(u => u.role === 'admin');

      // Beneficiary statistics
      const totalBeneficiaries = beneficiaries.length;
      const beneficiariesWithPending = beneficiaries.filter(b => (b.coupon_count || 0) - (b.redeemed_count || 0) > 0).length;
      const beneficiariesFullyRedeemed = beneficiaries.filter(b => (b.redeemed_count || 0) >= (b.coupon_count || 0)).length;

      setReportData({
        project: selectedProject,
        totalCoupons,
        redeemedCoupons,
        pendingCoupons,
        expiredCoupons,
        totalCouponValue,
        redeemedCouponValue,
        pendingCouponValue,
        vendorCount: vendorList.length,
        adminCount: adminList.length,
        totalBeneficiaries,
        beneficiariesWithPending,
        beneficiariesFullyRedeemed,
        beneficiaries,
        redemptionsMap,
        vendors: vendorList,
        admins: adminList,
        allUsers: projectUsers
      });
      } finally {
        // Restore original project context
        if (projectIdHeader) {
          localStorage.setItem('currentProjectId', projectIdHeader);
        } else {
          localStorage.removeItem('currentProjectId');
        }
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      setError('فشل تحميل بيانات التقرير');
      // Restore original project context even on error
      if (projectIdHeader) {
        localStorage.setItem('currentProjectId', projectIdHeader);
      } else {
        localStorage.removeItem('currentProjectId');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData) return;

    // Create CSV content (Excel can open CSV files)
    const rows = [];
    
    // Header
    rows.push(['تقرير المشروع', reportData.project?.name || '']);
    // Format report date in Gregorian calendar (YYYY-MM-DD)
    // Use UTC to avoid timezone issues
    const reportDate = new Date();
    const reportDateStr = `${reportDate.getUTCFullYear()}-${String(reportDate.getUTCMonth() + 1).padStart(2, '0')}-${String(reportDate.getUTCDate()).padStart(2, '0')}`;
    // Use single quote prefix to force Excel to treat it as text (prevents auto-conversion to Hijri)
    rows.push(['تاريخ التقرير', `'${reportDateStr}`]);
    rows.push([]);
    
    // Summary
    rows.push(['ملخص المشروع']);
    rows.push(['إجمالي القسائم', reportData.totalCoupons]);
    rows.push(['القسائم المستردة', reportData.redeemedCoupons]);
    rows.push(['القسائم المعلقة', reportData.pendingCoupons]);
    rows.push(['القسائم المنتهية', reportData.expiredCoupons]);
    rows.push(['إجمالي قيمة القسائم', reportData.totalCouponValue]);
    rows.push(['قيمة القسائم المستردة', reportData.redeemedCouponValue]);
    rows.push(['قيمة القسائم المعلقة', reportData.pendingCouponValue]);
    rows.push([]);
    
    // User Data
    rows.push(['بيانات المستخدمين']);
    rows.push(['عدد البائعين', reportData.vendorCount]);
    rows.push(['عدد المديرين', reportData.adminCount]);
    rows.push([]);
    
    // Beneficiary Data
    rows.push(['بيانات المستفيدين']);
    rows.push(['إجمالي المستفيدين', reportData.totalBeneficiaries]);
    rows.push(['مستفيدين بقسائم معلقة', reportData.beneficiariesWithPending]);
    rows.push(['مستفيدين مكتملين', reportData.beneficiariesFullyRedeemed]);
    rows.push([]);
    
    // Detailed Beneficiaries
    rows.push(['تفاصيل المستفيدين']);
    rows.push(['الاسم', 'رقم الهوية', 'الهاتف', 'عدد القسائم', 'مسترد', 'معلق', 'قيمة القسيمة', 'الحالة', 'تاريخ استرداد القسيمة']);
    reportData.beneficiaries.forEach(b => {
      const pending = (b.coupon_count || 0) - (b.redeemed_count || 0);
      const status = pending > 0 ? 'معلق' : 'مكتمل';
      
      // Get redemption timestamps for this beneficiary
      const redemptions = reportData.redemptionsMap?.[b.id] || [];
      
      // Debug: Log redemptions for this beneficiary
      if (redemptions.length > 0) {
        console.log(`Beneficiary ${b.id} (${b.name}): ${redemptions.length} redemptions`, redemptions);
        console.log(`First redemption keys:`, Object.keys(redemptions[0] || {}));
      }
      
      // Sort by timestamp descending (most recent first) and get all timestamps
      // Format dates in Gregorian calendar (YYYY-MM-DD HH:MM)
      // Use UTC to avoid timezone issues
      // Note: MySQL may return timestamp fields with different casing, check both
      const redemptionTimestamps = redemptions
        .map(r => {
          // Check for redemption_timestamp in various possible formats
          const timestamp = r.redemption_timestamp || r.redemptionTimestamp || r.timestamp || r.redemption_date || r.redemptionDate;
          
          if (!timestamp) {
            console.log(`Missing redemption_timestamp for redemption ID ${r.id}:`, Object.keys(r));
            return '';
          }
          
          const date = new Date(timestamp);
          // Check if date is valid
          if (isNaN(date.getTime())) {
            console.log(`Invalid date for redemption ID ${r.id}:`, timestamp);
            return '';
          }
          
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          const hours = String(date.getUTCHours()).padStart(2, '0');
          const minutes = String(date.getUTCMinutes()).padStart(2, '0');
          return `${year}-${month}-${day} ${hours}:${minutes}`;
        })
        .filter(ts => ts !== '')
        .join('; '); // Join multiple timestamps with semicolon
      
      // Use prefix to force Excel to treat it as text (prevents auto-conversion to Hijri)
      // Using single quote prefix which Excel recognizes as text indicator
      const finalRedemptionTimestamps = redemptionTimestamps ? `'${redemptionTimestamps}` : 'لا يوجد';
      
      rows.push([
        b.name || '',
        b.id_number || '',
        b.phone || '',
        b.coupon_count || 0,
        b.redeemed_count || 0,
        pending,
        b.coupon_value || 0,
        status,
        finalRedemptionTimestamps
      ]);
    });
    rows.push([]);
    
    // Vendor List
    rows.push(['قائمة البائعين']);
    rows.push(['اسم المستخدم', 'الدور']);
    reportData.vendors.forEach(v => {
      rows.push([v.username || '', v.role || '']);
    });
    rows.push([]);
    
    // Admin List
    rows.push(['قائمة المديرين']);
    rows.push(['اسم المستخدم', 'الدور']);
    reportData.admins.forEach(a => {
      rows.push([a.username || '', a.role || '']);
    });

    // Convert to CSV
    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    // Add BOM for UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `report_${reportData.project?.name || 'project'}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportRedemptionReportToExcel = async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      const projectIdHeader = localStorage.getItem('currentProjectId');
      localStorage.setItem('currentProjectId', selectedProject.id.toString());
      
      try {
        const response = await api.get('/reports/redemption', {
          headers: { 'X-Project-ID': selectedProject.id.toString() }
        });
        
        const data = response.data || [];
        
        // Group by beneficiary_id to avoid duplicates
        const beneficiaryMap = {};
        data.forEach(row => {
          if (!beneficiaryMap[row.beneficiary_id]) {
            beneficiaryMap[row.beneficiary_id] = {
              id: row.beneficiary_id,
              name: row.name,
              id_number: row.id_number,
              phone: row.phone,
              entry_date: row.entry_date,
              coupon_count: row.coupon_count,
              redeemed_count: row.redeemed_count,
              remaining_count: row.remaining_count,
              status: row.status,
              redemptions: []
            };
          }
          // Collect all redemptions for this beneficiary
          if (row.redemption_id && row.redemption_timestamp) {
            beneficiaryMap[row.beneficiary_id].redemptions.push({
              vendor: row.redeemed_by_vendor_id || '',
              timestamp: row.redemption_timestamp
            });
          }
        });
        
        // Convert map to array
        const beneficiaries = Object.values(beneficiaryMap);
        
        // Create CSV content
        const rows = [];
        
        // Header
        rows.push(['تقرير الاستلام', selectedProject.name]);
        const reportDate = new Date();
        const reportDateStr = `${reportDate.getUTCFullYear()}-${String(reportDate.getUTCMonth() + 1).padStart(2, '0')}-${String(reportDate.getUTCDate()).padStart(2, '0')}`;
        rows.push(['تاريخ التقرير', `'${reportDateStr}`]);
        rows.push([]);
        
        // Column headers
        rows.push(['الاسم', 'رقم الهوية', 'رقم الهاتف', 'المورد', 'تاريخ الإدخال', 'تاريخ ووقت الاستلام', 'عدد الكوبونات المخصصة', 'عدد الكوبونات المستلمة', 'عدد الكوبونات المتبقية', 'الحالة']);
        
        // Data rows - one row per beneficiary
        beneficiaries.forEach(beneficiary => {
          // Format entry date
          const entryDate = beneficiary.entry_date ? new Date(beneficiary.entry_date) : null;
          const entryDateStr = entryDate && !isNaN(entryDate.getTime())
            ? `'${entryDate.getUTCFullYear()}-${String(entryDate.getUTCMonth() + 1).padStart(2, '0')}-${String(entryDate.getUTCDate()).padStart(2, '0')}`
            : '';
          
          // Get all redemption timestamps and vendors
          const redemptions = beneficiary.redemptions || [];
          const redemptionTimestamps = redemptions
            .map(r => {
              if (!r.timestamp) return '';
              const date = new Date(r.timestamp);
              return !isNaN(date.getTime())
                ? `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
                : '';
            })
            .filter(ts => ts !== '')
            .join('; ');
          
          const vendors = redemptions.map(r => r.vendor || '').filter(v => v !== '').join('; ');
          
          rows.push([
            beneficiary.name || '',
            beneficiary.id_number || '',
            beneficiary.phone || '',
            vendors || 'لا يوجد',
            entryDateStr || 'لا يوجد',
            redemptionTimestamps ? `'${redemptionTimestamps}` : 'لا يوجد',
            beneficiary.coupon_count || 0,
            beneficiary.redeemed_count || 0,
            beneficiary.remaining_count || 0,
            beneficiary.status || ''
          ]);
        });
        
        // Convert to CSV
        const csvContent = rows.map(row => 
          row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        
        // Add BOM for UTF-8
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `redemption_report_${selectedProject.name}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        if (projectIdHeader) {
          localStorage.setItem('currentProjectId', projectIdHeader);
        } else {
          localStorage.removeItem('currentProjectId');
        }
      } catch (error) {
        console.error('Error exporting redemption report:', error);
        setError('فشل تصدير تقرير الاستلام');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportSummaryToPDF = async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      const projectIdHeader = localStorage.getItem('currentProjectId');
      localStorage.setItem('currentProjectId', selectedProject.id.toString());
      
      try {
        // Use redemption report endpoint to get all required data
        const response = await api.get('/reports/redemption', {
          headers: { 'X-Project-ID': selectedProject.id.toString() }
        });
        
        const data = response.data || [];
        
        // Group by beneficiary to show all redemptions
        const beneficiaryMap = {};
        data.forEach(row => {
          if (!beneficiaryMap[row.beneficiary_id]) {
            beneficiaryMap[row.beneficiary_id] = {
              id: row.beneficiary_id,
              name: row.name,
              id_number: row.id_number,
              phone: row.phone,
              entry_date: row.entry_date,
              coupon_count: row.coupon_count,
              redeemed_count: row.redeemed_count,
              remaining_count: row.remaining_count,
              status: row.status,
              redemptions: []
            };
          }
          if (row.redemption_id) {
            beneficiaryMap[row.beneficiary_id].redemptions.push({
              vendor: row.redeemed_by_vendor_id,
              timestamp: row.redemption_timestamp
            });
          }
        });
        
        const beneficiaries = Object.values(beneficiaryMap);
        
        // Create PDF directly using jsPDF - simpler and more reliable
        const pdf = new jsPDF('landscape', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        // Report header
        const reportDate = new Date();
        const reportDateStr = `${reportDate.getUTCFullYear()}-${String(reportDate.getUTCMonth() + 1).padStart(2, '0')}-${String(reportDate.getUTCDate()).padStart(2, '0')}`;
        
        // Add header using html2canvas for better Arabic support
        const headerDiv = document.createElement('div');
        headerDiv.style.width = '800px';
        headerDiv.style.padding = '20px';
        headerDiv.style.backgroundColor = '#f9fafb';
        headerDiv.style.fontFamily = 'Arial, sans-serif';
        headerDiv.style.direction = 'rtl';
        headerDiv.style.textAlign = 'center';
        headerDiv.innerHTML = `
          <h1 style="font-size: 24px; font-weight: bold; margin-bottom: 15px; color: #1f2937;">تقرير الاستلام</h1>
          <div style="text-align: right; font-size: 16px; margin: 10px 0;">
            <strong>اسم المشروع:</strong> ${selectedProject.name || 'غير محدد'}
          </div>
          <div style="text-align: right; font-size: 16px; margin: 10px 0;">
            <strong>تاريخ التقرير:</strong> ${reportDateStr}
          </div>
          <div style="text-align: right; font-size: 14px; margin: 10px 0; color: #4b5563;">
            <strong>إجمالي المستفيدين:</strong> ${beneficiaries.length}
          </div>
        `;
        
        document.body.appendChild(headerDiv);
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const headerCanvas = await html2canvas(headerDiv, {
          scale: 2,
          backgroundColor: '#f9fafb',
          useCORS: true
        });
        document.body.removeChild(headerDiv);
        
        const headerImgData = headerCanvas.toDataURL('image/png');
        const headerHeight = (headerCanvas.height / headerCanvas.width) * (pdfWidth - 20);
        pdf.addImage(headerImgData, 'PNG', 10, 10, pdfWidth - 20, headerHeight);
        
        // Now create table content - process in batches to avoid canvas size limits
        const ROWS_PER_BATCH = 10; // Process 10 rows at a time
        let currentRow = 0;
        let currentPage = 0;
        let yPosition = headerHeight + 20; // Initialize yPosition after header
        
        while (currentRow < beneficiaries.length) {
          if (currentPage > 0) {
            pdf.addPage('landscape');
            yPosition = 10; // Reset position for new page
          }
          
          const batchEnd = Math.min(currentRow + ROWS_PER_BATCH, beneficiaries.length);
          const batch = beneficiaries.slice(currentRow, batchEnd);
          
          // Create table HTML for this batch
          const tableDiv = document.createElement('div');
          tableDiv.style.width = '1200px';
          tableDiv.style.padding = '20px';
          tableDiv.style.backgroundColor = 'white';
          tableDiv.style.fontFamily = 'Arial, sans-serif';
          tableDiv.style.direction = 'rtl';
          tableDiv.style.textAlign = 'right';
          
          tableDiv.innerHTML = `
            <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
              <thead>
                <tr style="background-color: #3b82f6; color: white;">
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: right; font-weight: bold;">الاسم</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: right; font-weight: bold;">رقم الهوية</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: right; font-weight: bold;">الهاتف</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: right; font-weight: bold;">المورد</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: right; font-weight: bold;">تاريخ الإدخال</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: right; font-weight: bold;">تاريخ الاستلام</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: center; font-weight: bold;">مخصصة</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: center; font-weight: bold;">مستلمة</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: center; font-weight: bold;">متبقية</th>
                  <th style="border: 1px solid #1e40af; padding: 8px; text-align: center; font-weight: bold;">الحالة</th>
                </tr>
              </thead>
              <tbody>
                ${batch.map((beneficiary, index) => {
                  const entryDate = beneficiary.entry_date ? new Date(beneficiary.entry_date) : null;
                  const entryDateStr = entryDate && !isNaN(entryDate.getTime())
                    ? `${entryDate.getUTCFullYear()}-${String(entryDate.getUTCMonth() + 1).padStart(2, '0')}-${String(entryDate.getUTCDate()).padStart(2, '0')}`
                    : 'لا يوجد';
                  
                  const redemptions = beneficiary.redemptions || [];
                  const redemptionTimestamps = redemptions
                    .map(r => {
                      if (!r.timestamp) return '';
                      const date = new Date(r.timestamp);
                      return !isNaN(date.getTime())
                        ? `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
                        : '';
                    })
                    .filter(ts => ts !== '')
                    .join('; ');
                  
                  const vendors = redemptions.map(r => r.vendor || '').filter(v => v !== '').join('; ');
                  const rowBgColor = (currentRow + index) % 2 === 0 ? '#ffffff' : '#f9fafb';
                  
                  return `
                    <tr style="background-color: ${rowBgColor};">
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: right;">${beneficiary.name || ''}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: right;">${beneficiary.id_number || ''}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: right;">${beneficiary.phone || ''}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: right;">${vendors || 'لا يوجد'}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: right;">${entryDateStr}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: right;">${redemptionTimestamps || 'لا يوجد'}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: center; font-weight: bold;">${beneficiary.coupon_count || 0}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: center; font-weight: bold;">${beneficiary.redeemed_count || 0}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: center; font-weight: bold;">${beneficiary.remaining_count || 0}</td>
                      <td style="border: 1px solid #d1d5db; padding: 6px; text-align: center; font-weight: bold; color: ${beneficiary.status === 'مكتمل' ? '#059669' : '#d97706'};">
                        ${beneficiary.status || ''}
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          `;
          
          document.body.appendChild(tableDiv);
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const tableCanvas = await html2canvas(tableDiv, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
          });
          
          document.body.removeChild(tableDiv);
          
          const tableImgData = tableCanvas.toDataURL('image/png');
          const tableHeight = (tableCanvas.height / tableCanvas.width) * (pdfWidth - 20);
          
          // Check if table fits on current page
          if (yPosition + tableHeight > pdfHeight - 10) {
            pdf.addPage('landscape');
            yPosition = 10;
          }
          
          pdf.addImage(tableImgData, 'PNG', 10, yPosition, pdfWidth - 20, tableHeight);
          yPosition += tableHeight + 10;
          
          currentRow = batchEnd;
          currentPage++;
        }
        
        // Save PDF
        pdf.save(`redemption_report_${selectedProject.name}_${new Date().toISOString().split('T')[0]}.pdf`);
        
        if (projectIdHeader) {
          localStorage.setItem('currentProjectId', projectIdHeader);
        } else {
          localStorage.removeItem('currentProjectId');
        }
      } catch (error) {
        console.error('Error exporting PDF report:', error);
        setError('فشل تصدير التقرير');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-extrabold text-gray-900 flex items-center">
          <FileText className="w-8 h-8 mr-3 text-blue-600" /> صفحة التقارير
        </h2>
        <button
          onClick={onClose}
          className="flex items-center text-sm text-white bg-gray-600 py-2 px-4 rounded-lg hover:bg-gray-700 transition"
        >
          <X className="w-4 h-4 mr-1" /> إغلاق
        </button>
      </div>

      {/* Project Selector */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">اختر المشروع:</label>
        <select
          value={selectedProject?.id || ''}
          onChange={(e) => {
            const project = projects.find(p => p.id === parseInt(e.target.value));
            setSelectedProject(project);
          }}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        >
          {projects.map(project => (
            <option key={project.id} value={project.id}>{project.name}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">جاري تحميل البيانات...</span>
        </div>
      ) : reportData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">إجمالي القسائم</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">{reportData.totalCoupons}</p>
                </div>
                <Tag className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">القسائم المستردة</p>
                  <p className="mt-1 text-3xl font-semibold text-green-600">{reportData.redeemedCoupons}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-amber-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">القسائم المعلقة</p>
                  <p className="mt-1 text-3xl font-semibold text-amber-600">{reportData.pendingCoupons}</p>
                </div>
                <Calendar className="w-8 h-8 text-amber-500" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">إجمالي قيمة القسائم</p>
                  <p className="mt-1 text-3xl font-semibold text-purple-600">{reportData.totalCouponValue.toLocaleString()}</p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-500" />
              </div>
            </div>
          </div>

          {/* Detailed Analytics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-500" /> إحصائيات القسائم
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">القسائم المنتهية:</span>
                  <span className="font-semibold text-red-600">{reportData.expiredCoupons}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">قيمة القسائم المستردة:</span>
                  <span className="font-semibold text-green-600">{reportData.redeemedCouponValue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">قيمة القسائم المعلقة:</span>
                  <span className="font-semibold text-amber-600">{reportData.pendingCouponValue.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2 text-indigo-500" /> بيانات المستخدمين
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">عدد البائعين:</span>
                  <span className="font-semibold text-indigo-600">{reportData.vendorCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">عدد المديرين:</span>
                  <span className="font-semibold text-indigo-600">{reportData.adminCount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Beneficiary Data */}
          <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-green-500" /> بيانات المستفيدين
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">إجمالي المستفيدين</p>
                <p className="text-2xl font-bold text-green-600">{reportData.totalBeneficiaries}</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">مستفيدين بقسائم معلقة</p>
                <p className="text-2xl font-bold text-amber-600">{reportData.beneficiariesWithPending}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">مستفيدين مكتملين</p>
                <p className="text-2xl font-bold text-blue-600">{reportData.beneficiariesFullyRedeemed}</p>
              </div>
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex justify-end gap-4 mb-6">
            <button
              onClick={exportToExcel}
              className="flex items-center bg-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-700 transition shadow-lg"
            >
              <Download className="w-5 h-5 mr-2" /> تقرير المشروع (Excel)
            </button>
            <button
              onClick={exportRedemptionReportToExcel}
              className="flex items-center bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition shadow-lg"
            >
              <FileDown className="w-5 h-5 mr-2" /> تقرير الاستلام (Excel)
            </button>
            <button
              onClick={exportSummaryToPDF}
              className="flex items-center bg-red-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-red-700 transition shadow-lg"
            >
              <FileText className="w-5 h-5 mr-2" /> تقرير مختصر (PDF)
            </button>
          </div>
        </>
      ) : (
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <p className="text-gray-600">اختر مشروعاً لعرض التقرير</p>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;

